/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Modal, ModalBody, ModalSidebar, Text } from 'bold-ui'
import { confirm } from 'components/modals/confirm'
import { MChatPerguntaEnum, MChatRiscoEnum } from 'graphql/types.generated'
import { useRef } from 'react'
import { metaPath } from 'util/metaPath'

import { ConsultaSeguimentoPerguntasDiffModalBody } from '../consulta-seguimento-aplicacao/ConsultaSeguimentoPerguntasDiffModalBody'
import { useConsultaSeguimentoAplicacao } from '../hooks/useConsultaSeguimentoAplicacao'
import { useConsultaSeguimentoModal } from '../hooks/useConsultaSeguimentoModal'
import { ConsultaSeguimentoFieldModel, ConsultaSeguimentoModalTab } from '../model-consultaSeguimento'
import { ConsultaSeguimentoModalFooter } from './ConsultaSeguimentoModalFooter'
import { ConsultaSeguimentoModalStepper } from './ConsultaSeguimentoModalStepper'
import { ConsultaSeguimentoModalTabAdicionar } from './ConsultaSeguimentoModalTabAdicionar'
import { ConsultaSeguimentoModalTabResultado } from './ConsultaSeguimentoModalTabResultado'
import { ConsultaSeguimentoModalTabSelecao } from './ConsultaSeguimentoModalTabSelecao'

interface ConsultaSeguimentoModalProps {
  valueInCache: ConsultaSeguimentoFieldModel
  mChatRisco: MChatRiscoEnum
  mChatPerguntasFalhas: MChatPerguntaEnum[]
  aplicarButtonRef: React.RefObject<HTMLButtonElement>
  onSave: (values: ConsultaSeguimentoFieldModel) => void
}

const path = metaPath<ConsultaSeguimentoFieldModel>()

export function ConsultaSeguimentoModal(props: Readonly<ConsultaSeguimentoModalProps>) {
  const { valueInCache, mChatRisco, mChatPerguntasFalhas, aplicarButtonRef, onSave } = props

  const backToSelecaoButtonRef = useRef<HTMLButtonElement>()
  const backToAplicacaoButtonRef = useRef<HTMLButtonElement>()
  const backQuestionButtonRef = useRef<HTMLButtonElement>()
  const modalContentRef = useRef<HTMLDivElement>()

  const numberOfPerguntasFalhasNoMChat = mChatPerguntasFalhas?.length
  const hasPerguntasFalhasNoMChat = numberOfPerguntasFalhasNoMChat > 0
  const isMChatRiscoBaixo = hasPerguntasFalhasNoMChat && numberOfPerguntasFalhasNoMChat < 3

  const {
    currentTab,
    handleCloseModal,
    handleGoToAplicacao,
    handleGoBackToAplicacao,
    handleGoToResultado,
    handleGoToSelecao,
  } = useConsultaSeguimentoModal({
    valueInCache,
    aplicarButtonRef,
    onConfirmCloseModal: confirmCloseModal,
  })

  const {
    selectionState,
    clearStartFailed,
    start,
    goToNextQuestion,
    goToPreviousQuestion,
  } = useConsultaSeguimentoAplicacao({
    modalContentRef,
    backQuestionButtonRef,
    backToAplicacaoButtonRef,
    backToSelecaoButtonRef,
    mChatPerguntasFalhas,
    onConfirmSelectionChanges: confirmSelectionChangesModal,
    onStart: handleGoToAplicacao,
    onFinish: handleGoToResultado,
    onSave,
  })

  return (
    <Modal open closeOnBackdropClick={false} onClose={handleCloseModal} style={styles.modal}>
      <ModalSidebar position='left' style={styles.sidebar}>
        <ConsultaSeguimentoModalStepper name={path.aplicacao} currentTab={currentTab} />
      </ModalSidebar>

      <ModalBody>
        <div ref={modalContentRef}>
          {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
            <ConsultaSeguimentoModalTabSelecao
              path={path}
              mChatRisco={mChatRisco}
              mChatPerguntasFalhas={mChatPerguntasFalhas}
              selectionState={selectionState}
              clearStartFailed={clearStartFailed}
            />
          )}
          {currentTab === ConsultaSeguimentoModalTab.APLICACAO && (
            <ConsultaSeguimentoModalTabAdicionar
              path={path}
              backToSelecaoButtonRef={backToSelecaoButtonRef}
              backQuestionButtonRef={backQuestionButtonRef}
              onGoBackToSelecao={handleGoToSelecao}
              onGoToPreviousQuestion={goToPreviousQuestion}
            />
          )}
          {currentTab === ConsultaSeguimentoModalTab.RESULTADO && (
            <ConsultaSeguimentoModalTabResultado
              value={valueInCache}
              backToAplicacaoButtonRef={backToAplicacaoButtonRef}
              onGoBackToAplicacao={handleGoBackToAplicacao}
            />
          )}
        </div>
      </ModalBody>

      <ConsultaSeguimentoModalFooter
        name={path}
        currentTab={currentTab}
        isMChatRiscoBaixo={isMChatRiscoBaixo}
        startFailed={selectionState.failed}
        onStart={start}
        onGoToNextQuestion={goToNextQuestion}
        onCloseModal={handleCloseModal}
      />
    </Modal>
  )
}

const confirmCloseModal = (onConfirm: () => void) => {
  confirm({
    title: 'Deseja sair sem finalizar a Entrevista de Seguimento?',
    icon: {
      name: 'exclamationTriangleOutline',
      fill: 'alert',
    },
    confirmLabel: 'Sair da tela',
    cancelLabel: 'Continuar aqui',
    type: 'primary',
    size: 'small',
    onConfirm: onConfirm,
    render: () => <Text>As alterações realizadas serão perdidas, inclusive resultados já calculados.</Text>,
  })()
}

const confirmSelectionChangesModal = async (
  perguntasAnteriores: MChatPerguntaEnum[],
  perguntasAtuais: MChatPerguntaEnum[],
  perguntasObrigatorias: MChatPerguntaEnum[],
  perguntasComRespostas: MChatPerguntaEnum[]
) => {
  return confirm({
    title: 'Deseja continuar a entrevista com estas alterações?',
    icon: {
      name: 'infoCircleOutline',
      fill: 'primary',
    },
    confirmLabel: 'Continuar entrevista',
    cancelLabel: 'Voltar',
    type: 'primary',
    size: 'auto',
    depthLevel: 2,
    render: () => (
      <ConsultaSeguimentoPerguntasDiffModalBody
        perguntasAnteriores={perguntasAnteriores}
        perguntasAtuais={perguntasAtuais}
        perguntasObrigatorias={perguntasObrigatorias}
        perguntasComRespostas={perguntasComRespostas}
      />
    ),
  })()
}

const styles = {
  modal: css`
    display: grid;
    grid-template-rows: 1fr auto;
    width: 62rem;
    max-width: 62rem;
    overflow: hidden;
    height: 80vh;
  `,
  sidebar: css`
    padding: 2rem 1.5rem 1.5rem 2rem;
  `,
}
